/**
 * GraphQL Voting Service
 * Handles voting poll CRUD and vote casting via AppSync
 */

import { generateClient } from 'aws-amplify/api';
import { VotingPoll, VotingPollStatus, Vote, VotingResult } from '../types/voting';
import { collectAcrossIdentities } from './identity';
import { GraphQLNotificationService } from './graphqlNotification';
import { NotificationCategory, NotificationPriority } from '../types/notification';

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const getAuthMode = (): { authMode: 'userPool' } => ({ authMode: 'userPool' as const });
const getData = (result: any): any => ('data' in result ? result.data : null);

// ---- GraphQL Queries ----

const getVotingPollQuery = /* GraphQL */ `
  query GetVotingPoll($id: ID!) {
    getVotingPoll(id: $id) {
      id title description createdBy createdByName status
      nominees nomineeNames voters voterNames deadline createdAt updatedAt
    }
  }
`;

const listVotingPollsQuery = /* GraphQL */ `
  query ListVotingPolls($limit: Int, $nextToken: String) {
    listVotingPolls(limit: $limit, nextToken: $nextToken) {
      items {
        id title description createdBy createdByName status
        nominees nomineeNames voters voterNames deadline createdAt updatedAt
      }
      nextToken
    }
  }
`;

const votingPollsByCreatorQuery = /* GraphQL */ `
  query VotingPollsByCreator($createdBy: String!, $limit: Int, $nextToken: String) {
    votingPollsByCreator(createdBy: $createdBy, limit: $limit, nextToken: $nextToken) {
      items {
        id title description createdBy createdByName status
        nominees nomineeNames voters voterNames deadline createdAt updatedAt
      }
      nextToken
    }
  }
`;

const votingPollsByVoterQuery = /* GraphQL */ `
  query VotingPollsByVoter($voterId: String!, $limit: Int, $nextToken: String) {
    votingPollsByVoter(voterId: $voterId, limit: $limit, nextToken: $nextToken) {
      items {
        id title description createdBy createdByName status
        nominees nomineeNames voters voterNames deadline createdAt updatedAt
      }
      nextToken
    }
  }
`;

const votesByPollQuery = /* GraphQL */ `
  query VotesByPoll($pollId: String!, $limit: Int, $nextToken: String) {
    votesByPoll(pollId: $pollId, limit: $limit, nextToken: $nextToken) {
      items {
        pollId voterId voterName nomineeId nomineeName createdAt
      }
      nextToken
    }
  }
`;

// ---- GraphQL Mutations ----

const createVotingPollMutation = /* GraphQL */ `
  mutation CreateVotingPoll($input: CreateVotingPollInput!) {
    createVotingPoll(input: $input) {
      id title description createdBy createdByName status
      nominees nomineeNames voters voterNames deadline createdAt updatedAt
    }
  }
`;

const castVoteMutation = /* GraphQL */ `
  mutation CastVote($input: CastVoteInput!) {
    castVote(input: $input) {
      pollId voterId voterName nomineeId nomineeName createdAt
    }
  }
`;

const closeVotingPollMutation = /* GraphQL */ `
  mutation CloseVotingPoll($id: ID!) {
    closeVotingPoll(id: $id) {
      id title status updatedAt
    }
  }
`;

const deleteVotingPollMutation = /* GraphQL */ `
  mutation DeleteVotingPoll($id: ID!) {
    deleteVotingPoll(id: $id) { id }
  }
`;

const updateVotingPollMutation = /* GraphQL */ `
  mutation UpdateVotingPoll($input: UpdateVotingPollInput!) {
    updateVotingPoll(input: $input) {
      id title description createdBy createdByName status
      nominees nomineeNames voters voterNames deadline createdAt updatedAt
    }
  }
`;

// ---- Helper ----

function mapPoll(item: any): VotingPoll {
  return {
    id: item.id,
    title: item.title || '',
    description: item.description || undefined,
    createdBy: item.createdBy || '',
    createdByName: item.createdByName || '',
    status: item.status as VotingPollStatus,
    nominees: item.nominees || [],
    nomineeNames: item.nomineeNames || [],
    voters: item.voters || [],
    voterNames: item.voterNames || [],
    deadline: item.deadline || undefined,
    createdAt: item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || new Date().toISOString(),
  };
}

// ---- Service ----

export class GraphQLVotingService {

  /** Create a voting poll and notify all voters */
  static async createVotingPoll(
    input: {
      title: string;
      description?: string;
      nominees: string[];
      nomineeNames: string[];
      voters: string[];
      voterNames: string[];
      deadline?: string;
    },
    createdBy: string,
    createdByName: string
  ): Promise<{ success: boolean; poll?: VotingPoll; errors: string[] }> {
    if (!input.title.trim()) return { success: false, errors: ['Title is required'] };
    if (input.nominees.length < 2) return { success: false, errors: ['At least 2 nominees required'] };
    if (input.voters.length < 1) return { success: false, errors: ['At least 1 voter required'] };

    try {
      const result = await getClient().graphql({
        query: createVotingPollMutation,
        variables: {
          input: {
            ...input,
            createdBy,
            createdByName,
          },
        },
        ...getAuthMode(),
      } as any);

      const data = getData(result);
      const poll = mapPoll(data?.createVotingPoll);

      // Send in-app notifications to all voters
      for (const voterId of input.voters) {
        try {
          await GraphQLNotificationService.createNotification({
            userId: voterId,
            category: NotificationCategory.NOMINATION,
            priority: NotificationPriority.HIGH,
            title: 'New Voting Poll',
            message: `${createdByName} has created a voting poll "${input.title}" and needs your vote.`,
            icon: '🗳️',
            link: `/voting/${poll.id}`,
            actorName: createdByName,
          });
        } catch (e) {
          console.warn('[Voting] Failed to notify voter:', voterId, e);
        }
      }

      // Send email notifications to voters using the shared email service
      try {
        const voterEmails: string[] = [];
        for (const voterId of input.voters) {
          if (voterId.includes('@')) voterEmails.push(voterId);
        }

        if (voterEmails.length > 0) {
          const { emailPollCreated } = await import('./emailNotification');
          await emailPollCreated(
            voterEmails,
            input.title,
            createdByName,
            input.nomineeNames,
            input.deadline,
            poll.id,
            input.description
          );
        }
      } catch (e) {
        console.warn('[Voting] Email notification failed (non-critical):', e);
      }

      return { success: true, poll, errors: [] };
    } catch (error: any) {
      console.error('[Voting] Create error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error?.message || 'Failed to create poll'] };
    }
  }

  /** Get a single voting poll */
  static async getVotingPoll(id: string): Promise<VotingPoll | undefined> {
    try {
      const result = await getClient().graphql({
        query: getVotingPollQuery,
        variables: { id },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      return data?.getVotingPoll ? mapPoll(data.getVotingPoll) : undefined;
    } catch (error) {
      console.error('[Voting] Get error:', error);
      return undefined;
    }
  }

  /** List all voting polls (for admin/reviewer) */
  static async listAllPolls(): Promise<VotingPoll[]> {
    try {
      const result = await getClient().graphql({
        query: listVotingPollsQuery,
        variables: { limit: 100 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      return (data?.listVotingPolls?.items || []).map(mapPoll);
    } catch (error) {
      console.error('[Voting] List error:', error);
      return [];
    }
  }

  /** Get polls created by a specific user */
  static async getMyPolls(createdBy: string): Promise<VotingPoll[]> {
    try {
      // Both email domains: polls created before the domain move are keyed to the old
      // address, so a creator who has switched would see none of their own polls.
      const rows = await collectAcrossIdentities(createdBy, async candidate => {
        const result = await getClient().graphql({
          query: votingPollsByCreatorQuery,
          variables: { createdBy: candidate, limit: 100 },
          ...getAuthMode(),
        } as any);
        return getData(result)?.votingPollsByCreator?.items || [];
      });
      return rows.map(mapPoll);
    } catch (error) {
      console.error('[Voting] getMyPolls error:', error);
      return [];
    }
  }

  /** Get polls where user is a voter */
  static async getPollsForVoter(voterId: string): Promise<VotingPoll[]> {
    try {
      const rows = await collectAcrossIdentities(voterId, async candidate => {
        const result = await getClient().graphql({
          query: votingPollsByVoterQuery,
          variables: { voterId: candidate, limit: 100 },
          ...getAuthMode(),
        } as any);
        return getData(result)?.votingPollsByVoter?.items || [];
      });
      return rows.map(mapPoll);
    } catch (error) {
      console.error('[Voting] getPollsForVoter error:', error);
      return [];
    }
  }

  /** Get votes for a poll (only for poll creator / admin) */
  static async getVotes(pollId: string): Promise<Vote[]> {
    try {
      const result = await getClient().graphql({
        query: votesByPollQuery,
        variables: { pollId, limit: 100 },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      return (data?.votesByPoll?.items || []).map((v: any) => ({
        pollId: v.pollId,
        voterId: v.voterId,
        voterName: v.voterName,
        nomineeId: v.nomineeId,
        nomineeName: v.nomineeName,
        createdAt: v.createdAt,
      }));
    } catch (error) {
      console.error('[Voting] getVotes error:', error);
      return [];
    }
  }

  /** Compute voting results from votes */
  static computeResults(votes: Vote[], nomineeNames: string[], nominees: string[]): VotingResult[] {
    const countMap: Record<string, number> = {};
    nominees.forEach(id => { countMap[id] = 0; });
    votes.forEach(v => { countMap[v.nomineeId] = (countMap[v.nomineeId] || 0) + 1; });
    return nominees.map((id, i) => ({
      nomineeId: id,
      nomineeName: nomineeNames[i] || id,
      voteCount: countMap[id] || 0,
    })).sort((a, b) => b.voteCount - a.voteCount);
  }

  /** Cast a vote */
  static async castVote(
    pollId: string,
    voterId: string,
    voterName: string,
    nomineeId: string,
    nomineeName: string
  ): Promise<{ success: boolean; errors: string[] }> {
    try {
      await getClient().graphql({
        query: castVoteMutation,
        variables: {
          input: { pollId, voterId, voterName, nomineeId, nomineeName },
        },
        ...getAuthMode(),
      } as any);
      return { success: true, errors: [] };
    } catch (error: any) {
      console.error('[Voting] castVote error:', error);
      return { success: false, errors: [error?.message || 'Failed to cast vote'] };
    }
  }

  /** Close a voting poll */
  static async closePoll(id: string): Promise<{ success: boolean; errors: string[] }> {
    try {
      await getClient().graphql({
        query: closeVotingPollMutation,
        variables: { id },
        ...getAuthMode(),
      } as any);
      return { success: true, errors: [] };
    } catch (error: any) {
      return { success: false, errors: [error?.message || 'Failed to close poll'] };
    }
  }

  /** Delete a voting poll */
  static async deletePoll(id: string): Promise<{ success: boolean; errors: string[] }> {
    try {
      await getClient().graphql({
        query: deleteVotingPollMutation,
        variables: { id },
        ...getAuthMode(),
      } as any);
      return { success: true, errors: [] };
    } catch (error: any) {
      return { success: false, errors: [error?.message || 'Failed to delete poll'] };
    }
  }

  /** Update a voting poll (add/remove voters, update title, etc.) */
  static async updatePoll(
    id: string,
    updates: {
      title?: string;
      description?: string;
      nominees?: string[];
      nomineeNames?: string[];
      voters?: string[];
      voterNames?: string[];
      deadline?: string;
    },
    updaterName?: string
  ): Promise<{ success: boolean; poll?: VotingPoll; errors: string[] }> {
    try {
      const result = await getClient().graphql({
        query: updateVotingPollMutation,
        variables: { input: { id, ...updates } },
        ...getAuthMode(),
      } as any);
      const data = getData(result);
      const poll = mapPoll(data?.updateVotingPoll);

      // Notify any newly added voters
      if (updates.voters && updates.voterNames && updaterName) {
        for (let i = 0; i < updates.voters.length; i++) {
          const voterId = updates.voters[i];
          try {
            await GraphQLNotificationService.createNotification({
              userId: voterId,
              category: NotificationCategory.NOMINATION,
              priority: NotificationPriority.HIGH,
              title: 'Voting Poll Updated',
              message: `${updaterName} has updated the voting poll "${poll.title}".`,
              icon: '🗳️',
              link: `/voting/${poll.id}`,
              actorName: updaterName,
            });
          } catch (e) {
            console.warn('[Voting] Failed to notify voter on update:', voterId, e);
          }
        }
      }

      return { success: true, poll, errors: [] };
    } catch (error: any) {
      console.error('[Voting] updatePoll error:', error);
      return { success: false, errors: [error?.errors?.[0]?.message || error?.message || 'Failed to update poll'] };
    }
  }
}
